import {
  Component,
  ElementRef,
  EventEmitter,
  Output,
  OnInit,
  OnDestroy,
  ViewChild,
  computed,
  signal,
  SimpleChanges,
  OnChanges,
  inject,
  ChangeDetectionStrategy
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  MatError,
  MatFormField,
  MatHint,
  MatInput,
  MatLabel,
  MatSuffix,
} from "@angular/material/input";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatIcon } from "@angular/material/icon";
import { MatOption, MatSelect } from "@angular/material/select";
import { MatRadioButton, MatRadioGroup } from "@angular/material/radio";
import { MatTooltip } from "@angular/material/tooltip";
import { MatChipsModule } from "@angular/material/chips";
import { ActivatedRoute } from "@angular/router";
import { ENTER, COMMA, SEMICOLON } from "@angular/cdk/keycodes";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { TourAnchorMatMenuDirective } from "ngx-ui-tour-md-menu";
import { KeyValuePair } from "../../../../shared/interfaces/key-value-pair.interface";
import { LorenConditionRelation } from "../../enums/loren-condition-relation.enum";
import { LorenAutomationActionEnum } from "../../enums/loren-automation-action.enum";
import { LorenAutomationPlan } from "../../interfaces/loren-automation-plan.interface";
import { LorenPageContentComponent } from "../../../../shared/modules/loren-page-content/loren-page-content.component";
import { LorenAutomationPlanSectionComponent } from "../loren-automation-plan-section/loren-automation-plan-section.component";
import { LorenAutomationPlanConditionsComponent } from "../loren-automation-plan-conditions/loren-automation-plan-conditions.component";
import { LorenAutofocusDirective } from "../../../../shared/directives/loren-autofocus-directive/loren-autofocus.directive";
import { LorenPlanTimePeriodConverterPipe } from "../../pipes/loren-plan-time-period-converter-pipe/loren-plan-time-period-converter.pipe";
import { LorenAutomationPlanService } from "../../services/loren-automation-plan-service/loren-automation-plan.service";
import { LorenAuthenticationService } from "../../../../shared/services/loren-authentication-service/loren-authentication.service";
import { LorenAutomationPlanUtilsService } from "../../services/loren-automation-plan-utils-service/loren-automation-plan-utils.service";
import { LorenSnackBarService } from "../../../../shared/services/loren-snack-bar-service/loren-snack-bar.service";
import { LorenUtilsService } from "../../../../shared/services/loren-utils-service/loren-utils.service";
import { LorenFeatureToggleService } from "../../../../shared/services/loren-feature-toggle-service/loren-feature-toggle.service";
import { LorenFeature } from "../../../../shared/enums/loren-feature.enum";
import { LorenErrorHandlingService } from "../../../../shared/services/loren-error-handling-service/loren-error-handling-service.service";
import { LorenBaseFeature } from "../../../../shared/base-components/loren-base-feature/loren-base-feature";
import { LorenAutomationPlanTourSteps } from "../../../../shared/constants/loren-tour-steps.constants";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import {MatCardTitle} from "@angular/material/card";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {SelectAllDirective} from "../../../../common/forms/select-all.directive";
import { LOREN_USER_ROLES, LOREN_USER_CLIENT_ROLES } from "../../../../shared/services/loren-authentication-service/constants/loren-user-roles.constants";

@Component({
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatChipsModule,
        MatError,
        MatHint,
        MatIcon,
        MatInput,
        MatFormField,
        MatLabel,
        MatOption,
        MatRadioGroup,
        MatRadioButton,
        MatSelect,
        MatSuffix,
        MatTooltip,
        TranslocoPipe,
        TourAnchorMatMenuDirective,
        LorenAutofocusDirective,
        SelectAllDirective,
        LorenPageContentComponent,
        LorenAutomationPlanSectionComponent,
        LorenAutomationPlanConditionsComponent,
        MatButton,
        MatIconButton,
        MatCheckbox,
        MatSlideToggle,
        MatCardTitle,
    ],
  selector: "loren-automation-plan-define",
  templateUrl: "./loren-automation-plan-define.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./loren-automation-plan-define.component.scss"],
})
export class LorenAutomationPlanDefineComponent
  extends LorenBaseFeature
  implements OnInit, OnDestroy, OnChanges
{
  private activatedRoute = inject(ActivatedRoute);
  private translocoService = inject(TranslocoService);
  private automationPlanService = inject(LorenAutomationPlanService);
  private authenticationService = inject(LorenAuthenticationService);
  private planTimePeriodConverterPipe = inject(LorenPlanTimePeriodConverterPipe);
  private snackBarService = inject(LorenSnackBarService);
  private errorHandlingService = inject(LorenErrorHandlingService);
  private featureToggleService = inject(LorenFeatureToggleService);

  @Output() ruleSubmitted = new EventEmitter();
  @ViewChild("ruleNameInput") ruleNameInput: ElementRef;

  readonly ActionsEnum = LorenAutomationActionEnum;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA, SEMICOLON];
  readonly nameAnchorId = this.getAnchorId("ap.create_plan_name");
  readonly delayAnchorId = this.getAnchorId("ap.create_plan_delay");
  readonly periodAnchorId = this.getAnchorId("ap.create_plan_period");
  readonly conditionsAnchorId = this.getAnchorId("ap.create_plan_conditions");
  readonly actionsAnchorId = this.getAnchorId("ap.create_plan_actions");

  private timeouts = [];

  userId = signal(this.authenticationService.getUserInfo().id);
  allData = signal(null);
  rule: LorenAutomationPlan;
  plan: LorenAutomationPlan;
  smsNotificationsEnabled = signal<boolean>(false);
  canSelectRuleScope =
      this.authenticationService.getRoles().some((r) => r.includes(LOREN_USER_ROLES.COMPANY_ADMIN)) ||
      this.authenticationService.getUserRoles().some(r => r.includes(LOREN_USER_CLIENT_ROLES.CLIENT_ADMIN));

  /// base form
  allDays = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  timeConditionOptions: KeyValuePair<number>[] = [];

  ///// fields
  ruleNameField = new FormControl("", [
    Validators.required,
    Validators.minLength(1),
    Validators.maxLength(100),
  ]);
  daysField = new FormControl([...this.allDays]);
  wholeDayField = new FormControl(true);
  startingTimeField = new FormControl("00:00");
  endingTimeField = new FormControl("23:59");
  delayField = new FormControl("");
  isCompanyLevelField = new FormControl(false);

  baseForm = new FormGroup({
    ruleName: this.ruleNameField,
    days: this.daysField,
    wholeDay: this.wholeDayField,
    startingTime: this.startingTimeField,
    endingTime: this.endingTimeField,
    delay: this.delayField,
    isCompanyLevel: this.isCompanyLevelField,
  });

  confirmDeleteDelay = signal<boolean>(false);

  /// conditions
  conditionsMode = signal<"new" | "adding" | "editing" | "deleting">("new");
  conditionToEdit = signal(null);
  binaryConditions = signal([]);
  geofenceConditions = signal([]);
  numericConditions = signal([]);
  deletingCondition = signal(null);
  allConditions = computed(() => [
    ...this.numericConditions(),
    ...this.binaryConditions(),
    ...this.geofenceConditions(),
  ]);
  logicalOperatorOptions = [
    {
      key: "automation_plans.any_are_met",
      value: LorenConditionRelation.ANY_IS_MET,
    },
    {
      key: "automation_plans.all_are_met",
      value: LorenConditionRelation.ALL_ARE_MET,
    },
  ];
  logicalOperatorField = new FormControl(LorenConditionRelation.ANY_IS_MET, [
    Validators.required,
  ]);
  logicalOperatorsForm = new FormGroup({
    logicalOperator: this.logicalOperatorField,
  });

  /// actions
  actions = signal([{ action: this.ActionsEnum.SEND_USER_PUSH_NOTIFICATION }]);
  recipients = signal<Set<string>>(new Set());
  sendPushNotificationField = new FormControl(true);
  sendEmailField = new FormControl(false);
  sendSMSNotificationField = new FormControl(false);
  soundField = new FormControl(false);
  notifyOnRecoveryField = new FormControl(false);
  recipientsField = new FormControl(" ");

  actionsForm = new FormGroup({
    recipients: this.recipientsField,
    sendPushNotification: this.sendPushNotificationField,
    sendEmail: this.sendEmailField,
    sendSMSNotification: this.sendSMSNotificationField,
    sound: this.soundField,
    notifyOnRecovery: this.notifyOnRecoveryField,
  });

  ngOnInit() {
    this.allData.set(this.activatedRoute.snapshot.data.plans);
    this.activatedRoute.params.subscribe((x) => {
      if (x.id) {
        this.automationPlanService
          .getPlan(x.id, this.userId())
          .subscribe((res) => {
            this.rule = res;
            this.populateForms();
          })
      } else {
        this.resetFormsToDefaults()
      }

      this.numericConditions.set(this.rule?.numericConditions || []);
      this.binaryConditions.set(this.rule?.binaryConditions || []);
      this.geofenceConditions.set(this.rule?.geofenceConditions || []);
      this.actions.set(
        this.rule?.actions || [
          { action: LorenAutomationActionEnum.SEND_USER_PUSH_NOTIFICATION },
        ]
      );
      this.timeouts.push(
        setTimeout(() => {
          this.ruleNameInput?.nativeElement?.focus();
          this.ruleNameInput?.nativeElement?.scrollIntoView();
        })
      );

      this.conditionsMode.set("new");
    });
    for (let i = 0; i < 12; i++) {
      const minutes = (i + 1) * 5 * 60;
      this.timeConditionOptions.push({
        key: `automation_plans.minutes`,
        value: minutes,
      });
    }

    for (let i = 2; i < 13; i++) {
      this.timeConditionOptions.push({
        key: `automation_plans.hours`,
        value: i * 3600,
      });
    }


    if (this.wholeDayField.value) {
      this.startingTimeField.disable();
      this.endingTimeField.disable();
    }

    this.wholeDayField.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((v) => {
          if (v) {
            this.startingTimeField.setValue("00:00");
            this.endingTimeField.setValue("23:59");
            this.startingTimeField.disable();
            this.endingTimeField.disable();
          } else {
            this.startingTimeField.enable();
            this.endingTimeField.enable();
          }
        });

    this.sendPushNotificationField.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((v) => {
          this.updateActions(this.ActionsEnum.SEND_USER_PUSH_NOTIFICATION, v);
        })

    this.sendEmailField.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((v) => {
          this.updateActions(this.ActionsEnum.SEND_USER_EMAIL, v);
        });

    this.sendSMSNotificationField.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((v) => {
          this.updateActions(this.ActionsEnum.SEND_USER_SMS, v);
        });

    this.sendSMSNotificationField.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((v) => {
          if (v) {
            if (this.recipients().size) {
              this.recipientsField.clearValidators()
            } else {
              this.recipientsField.setValidators(Validators.required);
            }
            this.recipientsField.enable();
          } else {
            this.recipientsField.clearValidators();
            this.recipientsField.disable();
          }
        });

    this.isCompanyLevelField.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((isCompanyLevel) => {
          if (isCompanyLevel) {
            this.sendSMSNotificationField.setValue(false);
            this.sendSMSNotificationField.disable();
            this.recipients.set(new Set());
          } else if (this.smsNotificationsEnabled()) {
            this.sendSMSNotificationField.enable();
          }
        });

    this.featureToggleService
        .userHasFeature(LorenFeature.SMS_NOTIFICATION)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
          this.smsNotificationsEnabled.set(res);
          if (!res) {
            this.recipientsField.disable();
            this.recipientsField.clearValidators();
          }
        });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.rule) {
      if (this.rule) {
        this.populateForms();
      } else {
        this.resetFormsToDefaults();
      }
      this.numericConditions.set(this.rule?.numericConditions || []);
      this.binaryConditions.set(this.rule?.binaryConditions || []);
      this.geofenceConditions.set(this.rule?.geofenceConditions || []);
      this.actions.set(this.rule?.actions || []);
      this.recipients.set(
        new Set(
          this.rule?.phoneNumbers.map((n) => this.normalizePhoneNumber(n))
        )
      );

      this.conditionsMode.set("new");

      this.ruleNameInput?.nativeElement?.focus();
    }
  }

  ngOnDestroy() {
    this.timeouts.forEach((t) => clearTimeout(t));
  }

  /// FORM METHODS
  submitRule() {
    this.plan = this.getPlanFromForm();
    if (this.rule) {
      this.editRule()
    } else {
      this.addRule()
    }
  }

  deleteRule() {
    this.automationPlanService
        .deletePlan(this.rule.id, this.userId())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.ruleSubmitted.emit();
            this.snackBarService.showSuccessMessage();
          },
          error: () => this.errorHandlingService.showSnackbarError(),
        });
  }

  getDays() {
    return this.daysField.value.map((v) => {
      const start = this.translocoService.translate(`days_of_week_short.${v}`);
      const endDay = this.allDays[this.allDays.findIndex((i) => i === v) + 1];
      const end = this.translocoService.translate(
        `days_of_week_short.${endDay || "monday"}`
      );
      return { start, end };
    });
  }

  private getTimeNumber(value: string) {
    const [hours, minutes] = value.split(":");
    return Number(hours) * 60 + Number(minutes);
  }

  private populateForms() {
    const actionsList = this.rule?.actions?.map((a) => a.action);

    this.binaryConditions.set(this.rule.binaryConditions);
    this.numericConditions.set(this.rule.numericConditions);
    this.geofenceConditions.set(this.rule.geofenceConditions);
    this.recipients.set(
      new Set(this.rule.phoneNumbers.map((n) => this.normalizePhoneNumber(n)))
    );

    this.baseForm.setValue({
      ruleName: this.rule.name,
      days: this.rule?.effectivePeriod?.daysOfWeek
        ? Object.keys(this.rule?.effectivePeriod?.daysOfWeek).filter(
            (d) => this.rule.effectivePeriod?.daysOfWeek[d]
          )
        : [],
      wholeDay:
        this.rule.effectivePeriod?.startTime === 0 &&
        this.rule.effectivePeriod?.endTime === 1439,
      startingTime: this.planTimePeriodConverterPipe.transform(
        this.rule.effectivePeriod?.startTime
      ),
      endingTime: this.planTimePeriodConverterPipe.transform(
        this.rule.effectivePeriod?.endTime
      ),
      delay: this.rule.performActionAfter.toString(),
      isCompanyLevel: this.rule.isCompanyLevel,
    });
    this.logicalOperatorsForm.setValue({
      logicalOperator: this.rule.logicalOperator,
    });
    this.actionsForm.setValue({
      recipients: " ",
      sendPushNotification: actionsList.includes(
        this.ActionsEnum.SEND_USER_PUSH_NOTIFICATION
      ),
      sendEmail: actionsList.includes(this.ActionsEnum.SEND_USER_EMAIL),
      sendSMSNotification: actionsList.includes(this.ActionsEnum.SEND_USER_SMS),
      sound: this.rule.sound,
      notifyOnRecovery: this.rule.notifyOnRecovery,
    });
  }

  private resetFormsToDefaults() {
    this.baseForm.reset({
      ruleName: "",
      days: [...this.allDays],
      wholeDay: true,
      startingTime: "00:00",
      endingTime: "23:59",
      isCompanyLevel: false,
    });
    this.logicalOperatorsForm.reset({
      logicalOperator: LorenConditionRelation.ANY_IS_MET,
    });
    this.actionsForm.reset({
      sendPushNotification: true,
      sendEmail: false,
      sound: false,
      notifyOnRecovery: false,
    });
  }

  private getPlanFromForm() {
    const daysOfWeek = {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    };
    this.allDays.forEach((day) => {
      daysOfWeek[day] = this.daysField.value.includes(day);
    });

    return {
      actions: this.actions(),
      binaryConditions: this.binaryConditions().map((c) => ({
        value: c.value,
        meteringId: c.meteringId,
      })),
      performActionAfter: +this.delayField.value,
      effectivePeriod: {
        startTime: this.startingTimeField.value
          ? this.getTimeNumber(this.startingTimeField.value)
          : 0,
        endTime: this.endingTimeField.value
          ? this.getTimeNumber(this.endingTimeField.value)
          : 1439,
        daysOfWeek,
      },
      enabled: true,
      isEditable: true,
      isCompanyLevel: this.isCompanyLevelField.value,
      geofenceConditions: this.geofenceConditions().map((c) => ({
        meteringId: c.meteringId,
        condition: c.condition,
        areaId: c.areaId,
      })),
      logicalOperator:
        this.logicalOperatorField.value === LorenConditionRelation.ALL_ARE_MET
          ? LorenConditionRelation.ALL_ARE_MET
          : LorenConditionRelation.ANY_IS_MET,
      name: this.ruleNameField.value,
      numericConditions: this.numericConditions().map((c) => ({
        meteringId: c.meteringId,
        condition: c.condition,
        value: c.value,
        warningValue: c.warningValue,
      })),
      phoneNumbers: Array.from(this.recipients()).filter((n) => n && n !== " "),
      sound: this.soundField.value,
      notifyOnRecovery: this.notifyOnRecoveryField.value,
    };
  }

  private addRule() {
    this.automationPlanService
        .createPlan(this.userId(), this.plan)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.ruleSubmitted.emit();
            this.snackBarService.showSuccessMessage();
          },
          error: () => this.errorHandlingService.showSnackbarError(),
        });
  }

  private editRule() {
    this.automationPlanService
        .updatePlan(this.rule.id, this.plan)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.ruleSubmitted.emit();
            this.snackBarService.showSuccessMessage();
          },
          error: () => this.errorHandlingService.showSnackbarError(),
        });
  }

  /// CONDITIONS METHODS
  submitCondition(condition) {
    if (condition) {
      switch (condition.mode) {
        case "editing":
          this.editCondition(condition);
          break;
        case "deleting":
          this.deleteCondition(condition.id);
          break;
        default:
          switch (condition.ruleType) {
            case "binary":
              this.binaryConditions.update((c) => [...c, condition]);
              break;
            case "geofence":
              this.geofenceConditions.update((c) => [...c, condition]);
              break;
            default:
              this.numericConditions.update((c) => [...c, condition]);
              break;
          }
          break;
      }
    }
    this.setConditionsMode("new");
    this.deletingCondition.set(null);
  }

  getCondition(condition) {
    return (
      LorenAutomationPlanUtilsService.getConditionText(
        condition,
        this.allData().assets,
        this.allData().geozones,
        this.translocoService
      ) ||
      this.translocoService.translate(
        "automation_plans_form.issue_with_condition"
      )
    );
  }

  getConditionAsset(condition) {
    return LorenUtilsService.getAssetFromMeteringId(
      this.allData().assets,
      condition.meteringId
    );
  }

  confirmDeleteCondition(condition) {
    this.conditionsMode.set("new");
    this.deletingCondition.set(condition.id);
  }

  setConditionsMode(mode, condition = null) {
    this.deletingCondition.set(null);
    const asset = LorenUtilsService.getAssetFromMeteringId(
      this.allData().assets,
      condition?.meteringId
    );
    if (condition && !asset) return;
    this.conditionsMode.set(mode);
    this.conditionToEdit.set(condition ? { ...condition, asset } : null);
  }

  deleteCondition(id) {
    const conds = this.findConditionInArray(id);
    conds.update((c) => c.filter((c) => c.id !== id));
  }

  private editCondition(newCondition) {
    const conditions = this.findConditionInArray(newCondition.id);
    const cond = [...conditions()];
    const editedCondition = cond.find((bc) => bc.id === newCondition.id);
    ["condition", "meteringId", "value", "warningValue", "areaId"].forEach(
      (f) => {
        editedCondition[f] = newCondition[f];
      }
    );
    conditions.set(cond);
  }

  private findConditionInArray(id) {
    return this.binaryConditions().find((c) => c.id === id)
      ? this.binaryConditions
      : this.geofenceConditions().find((c) => c.id === id)
      ? this.geofenceConditions
      : this.numericConditions;
  }

  /// ACTIONS METHODS
  getActionText(action: LorenAutomationActionEnum) {
    return LorenAutomationPlanUtilsService.getActionDescription(action);
  }

  updateActions(action: LorenAutomationActionEnum, v: boolean) {
    this.actions.update((value) => {
      if (v) {
        // Sometimes the same notification is set to true 2+ times. Handle those cases gracefully
        // by only adding it once to the actions array. To fix the root issue we need to prevent multiple sources
        // of truth calling this function
        // TODO: Migrate to single source of truth at some point in the future :)
        const exists = !!value.find(x => x.action === action);

        if(exists) {
          return value;
        }

        return [...value, { action: this.ActionsEnum[action] }];
      }

      return value.filter(x => x.action !== this.ActionsEnum[action]);
    });
  }

  addRecipient(value) {
    const cleanedUpNumber = value
      .trim()
      .replaceAll(" ", "")
      .replaceAll("-", "");
    const regex = new RegExp("^(0|\\+359|00359)([0-9]{9})$");
    const valid = regex.test(cleanedUpNumber);
    const normalizedNumber = this.normalizePhoneNumber(cleanedUpNumber);
    this.recipientsField.setErrors({
      phoneNumberError: cleanedUpNumber ? !valid : !this.recipients().size,
    });
    if (regex.test(cleanedUpNumber)) {
      this.recipients.update((v) => v.add(normalizedNumber));
      this.recipientsField.setValue(" ");
      this.recipientsField.clearValidators();
    }
  }

  pasteNumbers($event: ClipboardEvent) {
    this.recipientsField.setErrors({
      phoneNumberError: false,
    });
    $event.preventDefault();
    const items = $event.clipboardData.getData("Text").split(/[;,\n]/);
    items.forEach((i) => this.addRecipient(i));
  }

  removeRecipient(recipient) {
    this.recipients.update(
      (v) => new Set(Array.from(v).filter((i) => i !== recipient))
    );
    if (!this.recipients().size) {
      this.recipientsField.setValidators(Validators.required);
      this.recipientsField.setErrors({ required: true });
    }
  }

  private normalizePhoneNumber(phoneNumber: string) {
    return new RegExp("^0([0-9]{9})$").test(phoneNumber)
      ? phoneNumber.replace("0", "+359")
      : new RegExp("^00359([0-9]{9})$").test(phoneNumber)
      ? phoneNumber.replace("00", "+")
      : phoneNumber;
  }

  private getAnchorId(id: string) {
    return LorenAutomationPlanTourSteps.find((x) => x.anchorId === id).anchorId;
  }
}
